import { useState, useEffect, useCallback } from 'react'
import { Card } from '@trafiflow/shared-ui'
import { Save, AlertCircle, Info, Loader2, Plus, X, Building2, Users, Target, Globe, Trash2, Search } from 'lucide-react'
import apiClient from '@/shared/lib/api'
import { useAuth } from '@trafiflow/shared-ui'

// Competitor categories
const COMPETITOR_CATEGORIES = [
    'Direct Competitor',
    'Indirect Competitor',
    'Aspirational Brand',
    'Industry Leader',
    'Emerging Player',
    'Alternative Solution'
] as const

type CompetitorCategory = typeof COMPETITOR_CATEGORIES[number]

interface Competitor {
    id: string
    name: string
    website?: string
    facebookPageId?: string
    category: CompetitorCategory
    notes?: string
    addedAt: string
}

interface BrandOverview {
    businessName: string
    website: string
    primaryMarket: string
    brandSummary: string
    targetRegions: string[]
    targetCustomer: string
    productOffer: string
    competitors: Competitor[]
}

export function BrandContextEngineering() {
    const { user } = useAuth()
    const [overview, setOverview] = useState<BrandOverview>({
        businessName: '',
        website: '',
        primaryMarket: '',
        brandSummary: '',
        targetRegions: [],
        targetCustomer: '',
        productOffer: '',
        competitors: []
    })
    const [isSaving, setIsSaving] = useState(false)
    const [saved, setSaved] = useState(false)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // New competitor form state
    const [showAddCompetitor, setShowAddCompetitor] = useState(false)
    const [newCompetitor, setNewCompetitor] = useState({
        name: '',
        website: '',
        facebookPageId: '',
        facebookUrl: '',
        category: 'Direct Competitor' as CompetitorCategory,
        notes: ''
    })
    const [isLookingUp, setIsLookingUp] = useState(false)
    const [lookupError, setLookupError] = useState<string | null>(null)

    // Load initial value from company profile, with fallback to agentplatEntity
    const fetchProfile = useCallback(async () => {
        if (!user?.tenantId) return

        try {
            setLoading(true)
            const response = await apiClient.get('/my-company')
            const profile = response.data?.profile
            const agentplatEntity = response.data?.agentplatEntity

            const profileOverview = profile?.overview

            // Load existing profile data first
            const existingOverview: BrandOverview = {
                businessName: profileOverview?.businessName || '',
                website: profileOverview?.website || '',
                primaryMarket: profileOverview?.primaryMarket || '',
                brandSummary: profileOverview?.brandSummary || '',
                targetRegions: profileOverview?.targetRegions || [],
                targetCustomer: profileOverview?.targetCustomer || '',
                productOffer: profileOverview?.productOffer || '',
                competitors: profileOverview?.competitors || []
            }

            // Check if we have any profile data at all
            const hasAnyProfileData =
                existingOverview.businessName ||
                existingOverview.website ||
                existingOverview.brandSummary ||
                existingOverview.productOffer ||
                existingOverview.targetCustomer ||
                existingOverview.primaryMarket ||
                existingOverview.targetRegions.length > 0 ||
                existingOverview.competitors.length > 0

            if (hasAnyProfileData) {
                // Use existing profile data - don't modify anything
                console.log('[BrandContext] Loading existing profile data')
                setOverview(existingOverview)
            } else if (agentplatEntity) {
                // No profile data exists - pre-fill form from agentplatEntity for convenience
                // NOTE: This does NOT auto-save. User must click "Save All Changes" to persist.
                console.log('[BrandContext] No profile data found, pre-filling form from agentplatEntity (not saved until user clicks Save)')
                const entityMetadata = agentplatEntity.metadata || {}
                const entityProfile = entityMetadata.profile?.overview || {}

                // Extract data from entity for display only
                const entityName = agentplatEntity.name || entityMetadata.name || ''
                const entityDomain = agentplatEntity.domain || ''
                const entityWebsite = entityProfile.website || entityMetadata.website ||
                    (entityDomain ? (entityDomain.startsWith('http') ? entityDomain : `https://${entityDomain}`) : '')
                const entityDescription = entityMetadata.description || entityProfile.brandSummary || ''
                const entityProducts = entityProfile.productOffer || entityMetadata.products_summary || ''
                const entityMarket = entityProfile.primaryMarket || entityMetadata.primary_market || ''

                setOverview({
                    businessName: entityName,
                    website: entityWebsite,
                    primaryMarket: entityMarket,
                    brandSummary: entityDescription,
                    targetRegions: entityProfile.targetRegions || [],
                    targetCustomer: entityProfile.targetCustomer || '',
                    productOffer: entityProducts,
                    competitors: entityProfile.competitors || []
                })
            }
        } catch (err) {
            console.error('Failed to fetch company profile:', err)
            setError('Failed to load existing brand context')
        } finally {
            setLoading(false)
        }
    }, [user?.tenantId])

    useEffect(() => {
        fetchProfile()
    }, [fetchProfile])

    const handleSave = async () => {
        if (!user?.tenantId) return

        setIsSaving(true)
        setError(null)
        setSaved(false)

        try {
            // First fetch current profile so we don't overwrite everything
            const response = await apiClient.get('/my-company')
            const currentProfile = response.data?.profile || { overview: {}, analyses: {} }

            const updatedProfile = {
                ...currentProfile,
                overview: {
                    ...currentProfile.overview,
                    businessName: overview.businessName.trim(),
                    website: overview.website.trim(),
                    primaryMarket: overview.primaryMarket.trim(),
                    brandSummary: overview.brandSummary.trim(),
                    targetRegions: overview.targetRegions, // Was missing - caused data loss!
                    targetCustomer: overview.targetCustomer.trim(),
                    productOffer: overview.productOffer.trim(),
                    competitors: overview.competitors
                }
            }

            await apiClient.put('/my-company', {
                tenantId: user.tenantId,
                profile: updatedProfile
            })

            setSaved(true)
            setTimeout(() => setSaved(false), 3000)
        } catch (e) {
            console.error('Failed to save brand context:', e)
            setError('Failed to save brand context')
        } finally {
            setIsSaving(false)
        }
    }

    const handleAddCompetitor = () => {
        if (!newCompetitor.name.trim()) return

        const competitor: Competitor = {
            id: `comp_${Date.now()}`,
            name: newCompetitor.name.trim(),
            website: newCompetitor.website.trim() || undefined,
            facebookPageId: newCompetitor.facebookPageId.trim() || undefined,
            category: newCompetitor.category,
            notes: newCompetitor.notes.trim() || undefined,
            addedAt: new Date().toISOString()
        }

        setOverview(prev => ({
            ...prev,
            competitors: [...prev.competitors, competitor]
        }))

        // Reset form
        setNewCompetitor({
            name: '',
            website: '',
            facebookPageId: '',
            facebookUrl: '',
            category: 'Direct Competitor',
            notes: ''
        })
        setLookupError(null)
        setShowAddCompetitor(false)
    }

    const handleRemoveCompetitor = (id: string) => {
        setOverview(prev => ({
            ...prev,
            competitors: prev.competitors.filter(c => c.id !== id)
        }))
    }

    const handleLookupFacebookPageId = async () => {
        // Determine the URL to lookup - prefer explicit FB URL, fall back to name-based
        const lookupUrl = newCompetitor.facebookUrl?.trim() ||
            (newCompetitor.name ? `https://www.facebook.com/${newCompetitor.name.replace(/\s+/g, '')}` : '')

        if (!lookupUrl) {
            setLookupError('Enter a Facebook URL or competitor name first')
            return
        }

        setIsLookingUp(true)
        setLookupError(null)

        try {
            const response = await apiClient.post('/v1/lookup/facebook-page-id', {
                url: lookupUrl
            })

            const data = response.data
            if (data.error) {
                setLookupError(data.error)
            } else if (data.page_id) {
                setNewCompetitor(prev => ({
                    ...prev,
                    facebookPageId: data.page_id
                }))
            } else {
                setLookupError('Could not find Facebook Page ID')
            }
        } catch (err) {
            console.error('[BrandContext] Facebook lookup error:', err)
            setLookupError('Failed to lookup Facebook page')
        } finally {
            setIsLookingUp(false)
        }
    }

    if (loading) {
        return (
            <div className="p-6 max-w-5xl mx-auto flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
        )
    }

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-tf-text">Brand Context Engineering</h1>
                <p className="text-tf-text-muted mt-1">
                    Configure your brand profile, target customers, and track competitors for AI-powered insights.
                </p>
            </div>

            <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 rounded-lg p-4 flex gap-3 text-sm">
                <Info className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-600" />
                <div>
                    <p className="font-semibold mb-1">Developer Mode Only</p>
                    <p>
                        This configuration is used by AI models for Storyboard Generation, Ad Analysis, and Competitor Research.
                        Tracked competitors will appear in the Ads Research filter.
                    </p>
                </div>
            </div>

            {error && (
                <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    <p>{error}</p>
                </div>
            )}

            {/* Brand Information Section */}
            <Card className="p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <h2 className="text-lg font-semibold text-tf-text">Brand Information</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-tf-text mb-1">
                            Business Name
                        </label>
                        <input
                            type="text"
                            value={overview.businessName}
                            onChange={(e) => setOverview(prev => ({ ...prev, businessName: e.target.value }))}
                            placeholder="Your Company Name"
                            className="w-full px-3 py-2 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-tf-text mb-1">
                            Website
                        </label>
                        <input
                            type="text"
                            value={overview.website}
                            onChange={(e) => setOverview(prev => ({ ...prev, website: e.target.value }))}
                            placeholder="https://yoursite.com"
                            className="w-full px-3 py-2 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-tf-text mb-1">
                            Primary Market
                        </label>
                        <input
                            type="text"
                            value={overview.primaryMarket}
                            onChange={(e) => setOverview(prev => ({ ...prev, primaryMarket: e.target.value }))}
                            placeholder="e.g., United States, Europe"
                            className="w-full px-3 py-2 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-tf-text mb-1">
                            Product/Offer
                        </label>
                        <input
                            type="text"
                            value={overview.productOffer}
                            onChange={(e) => setOverview(prev => ({ ...prev, productOffer: e.target.value }))}
                            placeholder="e.g., Premium skincare products"
                            className="w-full px-3 py-2 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </Card>

            {/* Target Customer Section */}
            <Card className="p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-green-600" />
                    <h2 className="text-lg font-semibold text-tf-text">Target Customer</h2>
                </div>

                <div>
                    <label className="block text-sm font-medium text-tf-text mb-1">
                        Target Customer Description
                    </label>
                    <p className="text-xs text-tf-text-muted mb-2">
                        Describe your ideal customer persona. This helps AI tailor messaging and analysis.
                    </p>
                    <textarea
                        value={overview.targetCustomer}
                        onChange={(e) => setOverview(prev => ({ ...prev, targetCustomer: e.target.value }))}
                        placeholder="e.g., Health-conscious women aged 25-45 who prioritize natural ingredients and sustainable packaging. They are active on Instagram and value authenticity over celebrity endorsements."
                        className="w-full h-24 px-3 py-2 border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                </div>
            </Card>

            {/* Competitor Analysis Section */}
            <Card className="p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-purple-600" />
                        <h2 className="text-lg font-semibold text-tf-text">Competitor Analysis</h2>
                    </div>
                    <button
                        onClick={() => setShowAddCompetitor(true)}
                        className="flex items-center gap-1 px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Add Competitor
                    </button>
                </div>

                <p className="text-sm text-tf-text-muted mb-4">
                    Track competitors to filter their ads in the Research section. Add Facebook Page IDs to see their specific ads.
                </p>

                {/* Add Competitor Form */}
                {showAddCompetitor && (
                    <div className="mb-4 p-4 bg-tf-bg-muted rounded-lg border border-tf-border">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-tf-text">Add Competitor</h3>
                            <button onClick={() => setShowAddCompetitor(false)} className="text-tf-text-muted hover:text-tf-text">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-tf-text-muted mb-1">
                                    Competitor Name *
                                </label>
                                <input
                                    type="text"
                                    value={newCompetitor.name}
                                    onChange={(e) => setNewCompetitor(prev => ({ ...prev, name: e.target.value }))}
                                    placeholder="Brand name"
                                    className="w-full px-3 py-2 text-sm border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-tf-text-muted mb-1">
                                    Category
                                </label>
                                <select
                                    value={newCompetitor.category}
                                    onChange={(e) => setNewCompetitor(prev => ({ ...prev, category: e.target.value as CompetitorCategory }))}
                                    className="w-full px-3 py-2 text-sm border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                                >
                                    {COMPETITOR_CATEGORIES.map(cat => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-tf-text-muted mb-1">
                                    Website
                                </label>
                                <input
                                    type="text"
                                    value={newCompetitor.website}
                                    onChange={(e) => setNewCompetitor(prev => ({ ...prev, website: e.target.value }))}
                                    placeholder="https://competitor.com"
                                    className="w-full px-3 py-2 text-sm border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-tf-text-muted mb-1">
                                    Facebook URL (for lookup)
                                </label>
                                <input
                                    type="text"
                                    value={newCompetitor.facebookUrl}
                                    onChange={(e) => setNewCompetitor(prev => ({ ...prev, facebookUrl: e.target.value }))}
                                    placeholder="https://facebook.com/BrandName"
                                    className="w-full px-3 py-2 text-sm border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-tf-text-muted mb-1">
                                    Facebook Page ID
                                </label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={newCompetitor.facebookPageId}
                                        onChange={(e) => setNewCompetitor(prev => ({ ...prev, facebookPageId: e.target.value }))}
                                        placeholder="e.g., 123456789"
                                        className="flex-1 px-3 py-2 text-sm border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleLookupFacebookPageId}
                                        disabled={isLookingUp || (!newCompetitor.facebookUrl && !newCompetitor.name)}
                                        className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                        title="Lookup Facebook Page ID from URL or name"
                                    >
                                        {isLookingUp ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Search className="w-4 h-4" />
                                        )}
                                        Lookup
                                    </button>
                                </div>
                                {lookupError && (
                                    <p className="text-xs text-red-500 mt-1">{lookupError}</p>
                                )}
                                <p className="text-xs text-tf-text-muted mt-1">
                                    Enter a Facebook URL above or click Lookup to auto-detect from competitor name
                                </p>
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-medium text-tf-text-muted mb-1">
                                    Notes
                                </label>
                                <input
                                    type="text"
                                    value={newCompetitor.notes}
                                    onChange={(e) => setNewCompetitor(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Why are you tracking this competitor?"
                                    className="w-full px-3 py-2 text-sm border border-tf-border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2 mt-3">
                            <button
                                onClick={() => setShowAddCompetitor(false)}
                                className="px-3 py-1.5 text-sm text-tf-text-muted hover:text-tf-text transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddCompetitor}
                                disabled={!newCompetitor.name.trim()}
                                className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                            >
                                Add
                            </button>
                        </div>
                    </div>
                )}

                {/* Competitor List */}
                {overview.competitors.length === 0 ? (
                    <div className="text-center py-8 text-tf-text-muted">
                        <Target className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No competitors tracked yet</p>
                        <p className="text-xs mt-1">Add competitors to see their ads in the Research section</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {overview.competitors.map(competitor => (
                            <div key={competitor.id} className="flex items-center justify-between p-3 bg-tf-bg-card border border-tf-border rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-medium text-sm">
                                        {competitor.name.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <span className="font-medium text-tf-text">{competitor.name}</span>
                                            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                                                {competitor.category}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 text-xs text-tf-text-muted">
                                            {competitor.website && (
                                                <a href={competitor.website} target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 flex items-center gap-1">
                                                    <Globe className="w-3 h-3" />
                                                    {new URL(competitor.website).hostname}
                                                </a>
                                            )}
                                            {competitor.facebookPageId && (
                                                <span>FB: {competitor.facebookPageId}</span>
                                            )}
                                            {competitor.notes && (
                                                <span className="italic">{competitor.notes}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleRemoveCompetitor(competitor.id)}
                                    className="p-1.5 text-tf-text-muted hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </Card>

            {/* Brand Pre-prompt Section */}
            <Card className="p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                    <Info className="w-5 h-5 text-blue-600" />
                    <h2 className="text-lg font-semibold text-tf-text">Brand Pre-prompt</h2>
                </div>

                <label htmlFor="brand-context" className="block text-sm font-medium text-tf-text mb-1">
                    Global AI Instructions
                </label>
                <p className="text-xs text-tf-text-muted mb-3">
                    Enter instructions that AI should always consider. For example: "The brand tone is modern and energetic. Always prefer short, punchy copy."
                </p>

                <textarea
                    id="brand-context"
                    value={overview.brandSummary}
                    onChange={(e) => setOverview(prev => ({ ...prev, brandSummary: e.target.value }))}
                    placeholder="Enter brand context instructions here..."
                    className="w-full h-40 p-3 border border-tf-border rounded-lg bg-tf-bg-card font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 whitespace-pre-wrap"
                />

                <div className="text-xs text-tf-text-muted mt-2">
                    {overview.brandSummary.length} characters
                </div>
            </Card>

            {/* Save Button */}
            <div className="flex items-center justify-end gap-3 sticky bottom-4 bg-tf-bg-main/90 backdrop-blur p-4 -mx-6 rounded-lg border border-tf-border">
                {saved && (
                    <span className="text-sm text-green-600 font-medium">Saved successfully!</span>
                )}
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                    {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {isSaving ? 'Saving...' : 'Save All Changes'}
                </button>
            </div>

            {!overview.brandSummary.trim() && !overview.businessName.trim() && (
                <div className="mt-4 flex items-center gap-2 text-sm text-amber-600">
                    <AlertCircle className="w-4 h-4" />
                    No brand context is currently set. AI features will use their default prompts.
                </div>
            )}
        </div>
    )
}

export default BrandContextEngineering
